/**
 * THE QUOTE, LINE BY LINE.
 *
 * Anthony: "don't worry about parts markup percentage. Just let them pick
 * what they mark it up to in the customer price box. Customer should never
 * see our cost as recorded."
 *
 * So the matrix is a SUGGESTION and never a decision. The advisor types
 * what the customer pays. That is the right way round: a markup table
 * cannot know that this radiator was the last one in the county, or that
 * this customer is a fleet, or that the shop wants the job. A number the
 * software insists on is a number somebody works around, and working
 * around it means a second price living in somebody's head.
 *
 * WHAT COST IS FOR. It is recorded so the shop knows what it made, and it
 * is never sent to a customer — the garage endpoint builds its response
 * field by field and cost is not on the list. Two columns, two audiences,
 * and only one of them is on the invoice.
 */

import { db, api, configNow } from "../data/client.ts";
import { listLines, updateJobField, shopEpaId, type LineItem } from "../data/jobs.ts";
import { ensureTaxLine, isTaxLine } from "../domain/tax.ts";
import { sellPrice, type Tier } from "../domain/pricing.ts";
import { pickPart } from "../domain/parts.ts";
import { conditionOptions, asCondition, partsMissingCondition } from "../domain/partcondition.ts";
import { epaNag } from "../domain/hazardous.ts";
import { suppliesNag } from "../domain/shopsupplies.ts";
import {
  billAsOptions, asBillAs, separatePapersOn, billAsTitle, linesFor,
  papersNeedingConsent, consentWanted, type BillAs,
} from "../domain/billas.ts";
import { raiseInvoice } from "../data/payments.ts";
import { typeahead, loadVocabulary } from "../ui/typeahead.ts";
import { type Suggestion } from "../domain/services.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";
import { cap, anA } from "../domain/trade.ts";
import { shopZone } from "../ui/shopclock.ts";
import { hourIn } from "../domain/zoneclock.ts";
import {
  caseRate, investigatorsFor, CASE_MIN_RATE, CASE_TWO_RATE, CASE_MIN_HOURS, CASE_VEHICLE_FEE,
} from "../domain/casequote.ts";

const KINDS = ["labour", "part", "fee", "discount"] as const;
type Kind = (typeof KINDS)[number];

/**
 * THE PRICES THE SHOP ACTUALLY HAS.
 *
 * Anthony: "$150 an hour $300/hr between 10pm-6am for overnight. $225 for
 * ppi, $225 for smoke or compression test."
 *
 * Every one of those numbers has been living in somebody's head. The
 * `flat_prices` table has existed since the fourth migration and has never
 * held a row, so a pre-purchase inspection was typed from memory onto
 * every quote, and Nicole Trejo's night call-out had to be built with a
 * script because there was no way to press "night rate" on a screen.
 *
 * A price that only two people remember is a price that gets quoted two
 * different ways on the same day.
 */
export type FlatPrice = { label: string; price: number };
export type ShopPrices = {
  rate: number;
  nightRate: number | null;
  nightFrom: number;
  nightTo: number;
  flat: FlatPrice[];
};

/** Is it, right now where the shop is, the after-hours rate? */
export function isNightNow(from: number, to: number, at = new Date(), zone = shopZone()): boolean {
  // The shop's clock, not Pacific: Wrench in Albuquerque was an hour early
  // into its night rate and an hour late out of it. hourIn, not hour12:false,
  // which renders midnight as 24 on some builds.
  const h = Math.floor(hourIn(zone, at));
  // The window wraps past midnight, which is the whole point of it.
  return from > to ? (h >= from || h < to) : (h >= from && h < to);
}

export async function loadPrices(): Promise<ShopPrices> {
  const sb = await db();
  const [acct, flat] = await Promise.all([
    sb.from("accounts")
      .select("labour_rate,night_labour_rate,night_from_hour,night_to_hour")
      .limit(1).maybeSingle(),
    sb.from("flat_prices").select("label,price").eq("active", true).order("label"),
  ]);
  const a = (acct.data ?? {}) as Record<string, unknown>;
  const num = (v: unknown, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  const night = Number(a["night_labour_rate"]);
  return {
    rate: num(a["labour_rate"], 150),
    nightRate: Number.isFinite(night) && night > 0 ? night : null,
    nightFrom: num(a["night_from_hour"], 22),
    // 6am is a legitimate zero-ish hour, so it does not go through num().
    nightTo: Number.isFinite(Number(a["night_to_hour"])) ? Number(a["night_to_hour"]) : 6,
    flat: ((flat.data ?? []) as Array<Record<string, unknown>>)
      .map((f) => ({ label: String(f["label"]), price: Number(f["price"]) }))
      .filter((f) => f.label && Number.isFinite(f.price) && f.price > 0),
  };
}

/**
 * THE FIRST TIME DISCOUNT.
 *
 * Anthony: "If hemet and san jacinto locals push back on price we also
 * will offer a $25 discount for first time customers."
 *
 * A concession an advisor reaches for while somebody is on the phone
 * saying the number is too high, which means it has to be one press. An
 * advisor doing mental arithmetic on a call types the wrong number, and a
 * hand-typed discount is one nobody can count later — this way every one
 * of them is a `discount` line the reports can add up, so "what did the
 * first-time offer cost us" is answerable.
 *
 * IT ONLY OFFERS ITSELF TO A FIRST-TIME CUSTOMER. Somebody who has had
 * work done here before is not new, and the button is simply not there.
 * Nothing stops an advisor typing a discount by hand for a good reason.
 */
export const FIRST_TIME_OFF = 25;

export async function isFirstTimeCustomer(jobId: string): Promise<boolean> {
  const sb = await db();
  const { data: job } = await sb.from("jobs")
    .select("customer_id").eq("id", jobId).maybeSingle();
  const customerId = (job as { customer_id: string | null } | null)?.customer_id;
  if (!customerId) return false;

  // Work they have actually had done, not quotes they were sent. A
  // customer with three open estimates and nothing finished has still
  // never bought anything here.
  const { count } = await sb.from("jobs")
    .select("id", { count: "exact", head: true })
    .eq("customer_id", customerId)
    .in("status", ["completed", "invoiced", "paid"])
    .neq("id", jobId);
  return (count ?? 0) === 0;
}

export async function loadTiers(): Promise<Tier[]> {
  const sb = await db();
  const { data } = await sb.from("markup_tiers")
    .select("cost_from,cost_to,multiplier,min_margin").order("cost_from");
  return (data ?? []) as Tier[];
}

export async function saveLine(
  jobId: string,
  /**
   * `part_number` undefined means LEAVE IT ALONE — the field only exists
   * on a part line, and an absent input must not wipe a number that is
   * already stored. null means clear it; a string sets it.
   */
  l: { id?: string; kind: Kind; description: string; quantity: number; unit_price: number;
       /**
        * null clears it, a number sets it, and UNDEFINED leaves it alone —
        * the same rule `part_number` already follows. A row for a labor
        * line does not draw the cost box at all, and an absent box must
        * not wipe a figure somebody typed when it was a part.
        */
       cost?: number | null; part_number?: string | null;
       /**
        * Same rule again. Undefined means the box was never drawn, which
        * happens on every labour line, and must not wipe a condition that
        * is already stored against a part.
        */
       part_condition?: string | null;
       /** Which document this line prints on. Same undefined rule again. */
       bill_as?: string | null },
): Promise<void> {
  const sb = await db();
  if (!l.description.trim()) throw new Error("Give the line a description — it is what the customer reads.");
  /* NO CASE-FILE HOUR UNDER $240 (casequote.ts). Raised here, where every
     path that writes a line passes, rather than in each button. */
  if (shopWords().caseFile && l.kind === "labour") l = { ...l, unit_price: caseRate(l.unit_price) };

  if (l.id) {
    const { error } = await sb.from("line_items").update({
      kind: l.kind, description: l.description.trim(), quantity: l.quantity,
      unit_price: l.unit_price,
      // Undefined means the box was never drawn, so leave what is stored.
      // Only an explicit null clears it. Same rule for both.
      ...(l.cost === undefined ? {} : { cost: l.cost }),
      ...(l.part_number === undefined ? {} : { part_number: l.part_number }),
      ...(l.part_condition === undefined ? {} : { part_condition: l.part_condition }),
      ...(l.bill_as === undefined ? {} : { bill_as: l.bill_as }),
    }).eq("id", l.id);
    if (error) throw new Error(error.message);
    // A priced line changed, so the tax line (if this shop charges tax) is
    // now out of step. Editing the tax line itself must not trigger this.
    if (!isTaxLine(l.description)) await ensureTaxLine(jobId);
    return;
  }

  // Who, and where the line goes, asked at the same time rather than one
  // after the other: adding a line waited on each of these in turn.
  const [{ data: prof }, { data: existing }] = await Promise.all([
    sb.auth.getUser().then(({ data: me }) => sb.from("profiles")
      .select("account_id").eq("id", me.user?.id ?? "").maybeSingle()),
    sb.from("line_items").select("position").eq("job_id", jobId),
  ]);
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Your login is not attached to a shop.");

  const next = ((existing ?? []) as Array<{ position: number }>)
    .reduce((n, r) => Math.max(n, Number(r.position ?? 0)), -1) + 1;

  const { error } = await sb.from("line_items").insert({
    account_id: accountId, job_id: jobId, kind: l.kind,
    description: l.description.trim(), quantity: l.quantity,
    unit_price: l.unit_price, cost: l.cost ?? null, part_number: l.part_number ?? null,
    part_condition: l.part_condition ?? null,
    bill_as: l.bill_as ?? "repair",
    position: next,
  });
  if (error) throw new Error(error.message);
  if (!isTaxLine(l.description)) await ensureTaxLine(jobId);
}

export async function removeLine(id: string): Promise<void> {
  const sb = await db();
  // Know what is going before it goes: if the removed line was a priced
  // one, the tax line needs recomputing; if it WAS the tax line, the shop
  // meant to drop it, so leave it dropped.
  const { data: gone } = await sb.from("line_items")
    .select("job_id,description").eq("id", id).maybeSingle();
  const row = gone as { job_id: string; description: string } | null;
  const { error } = await sb.from("line_items").delete().eq("id", id);
  if (error) throw new Error(error.message);
  if (row && !isTaxLine(row.description)) await ensureTaxLine(row.job_id);
}

/** The editor, rendered into a host on the job card. */
/** What the book-time lookup needs to know about the car and the fault. */
/**
 * `found` is the technician's own tests, already summarized — what they
 * ran, what failed, and what each pass ruled out. It is separate from
 * `diagnosis` because it has a different standing: diagnosis is whatever
 * is written on the job, and this is a record of work somebody did with
 * verdicts attached. The drafter is told to trust it over the complaint.
 */
export type LabourContext = {
  vehicle: string; complaint: string; diagnosis?: string; found?: string;
};


/* ── book time ────────────────────────────────────────────────────── */

/**
 * ASKED FOR, NEVER APPLIED. The lookup answers with hours; the advisor
 * decides whether they become lines. A labor time is money, and a wrong
 * one is money the shop loses quietly on every job like it.
 *
 * Hidden entirely when the deployment has no AI key, because a button that
 * cannot work is worse than no button.
 */
function canAskBookTime(ctx?: LabourContext): boolean {
  if (!ctx?.vehicle?.trim()) return false;
  if (!ctx.complaint?.trim() && !ctx.diagnosis?.trim()) return false;
  try { return configNow().features.labour; } catch { return false; }
}

async function askBookTime(
  ctx: LabourContext, jobId: string, labourRate: number, onChange: () => void,
): Promise<void> {
  const out = document.getElementById("booktime-out");
  const btn = document.getElementById("booktime") as HTMLButtonElement | null;
  if (!out) return;
  if (btn) { btn.disabled = true; btn.textContent = "Looking…"; }
  out.innerHTML = `<div class="loading">Asking…</div>`;

  const done = () => { if (btn) { btn.disabled = false; btn.textContent = "Look up book time"; } };

  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    const res = await fetch(api("/api/labour"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify(ctx),
    });
    const body = await res.json() as {
      lines?: Array<{ task: string; hours: number; diagnostic: boolean }>;
      notes?: string[]; caveat?: string; error?: string;
    };
    done();
    if (!res.ok || !body.lines) {
      out.innerHTML = `<p class="msg bad">${esc(body.error ?? "That did not answer.")}</p>`;
      return;
    }

    out.innerHTML = `
      <div class="booktime">
        <div class="bt-h">Suggested book time</div>
        ${body.lines.map((l, i) => `
          <label class="bt-row">
            <input type="checkbox" class="bt-pick" data-i="${i}" checked>
            <span class="bt-task">${esc(l.task)}${
              l.diagnostic ? ` <span class="pill">diagnosis</span>` : ""}</span>
            <span class="bt-hours">${l.hours.toFixed(2)} h</span>
            <span class="bt-money">${money(l.hours * labourRate)}</span>
          </label>`).join("")}
        ${(body.notes ?? []).map((n) => `<div class="bt-note">${esc(n)}</div>`).join("")}
        <div class="bt-act">
          <button class="btn primary sm" id="bt-add">Add the ticked ones</button>
          <button class="btn ghost sm" id="bt-drop">Discard</button>
        </div>
        <div class="fine">${esc(body.caveat ?? "")}</div>
      </div>`;

    document.getElementById("bt-drop")?.addEventListener("click", () => { out.innerHTML = ""; });

    document.getElementById("bt-add")?.addEventListener("click", async () => {
      const add = document.getElementById("bt-add") as HTMLButtonElement;
      add.disabled = true; add.textContent = "Adding…";
      const picked = Array.from(document.querySelectorAll<HTMLInputElement>(".bt-pick"))
        .filter((c) => c.checked)
        .map((c) => body.lines![Number(c.dataset["i"])]!);
      try {
        for (const l of picked) {
          await saveLine(jobId, {
            kind: "labour", description: l.task, quantity: l.hours,
            unit_price: labourRate, cost: null, part_number: null,
          });
        }
        out.innerHTML = "";
        onChange();
      } catch (e) {
        add.disabled = false; add.textContent = "Add the ticked ones";
        out.insertAdjacentHTML("beforeend",
          `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
      }
    });
  } catch (e) {
    done();
    out.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  }
}

export function lineEditor(
  hostId: string, jobId: string, lines: LineItem[], tiers: Tier[],
  labourRate: number, onChange: () => void, ctx?: LabourContext,
  /** Called with the new grand total when a line is edited in place. */
  onTotals?: (total: number) => void,
): void {
  const host = document.getElementById(hostId);
  if (!host) return;

  /**
   * A QUOTE IS A DOCUMENT, SO IT IS LAID OUT LIKE ONE.
   *
   * Anthony: "line items for labor and parts look like shit."
   *
   * They did, and looking at eight of them together says why. Every line
   * was an identical bordered card of identical weight, and under every
   * one sat a row of labeled boxes including a KIND dropdown that said
   * "part" eight times in a column. A transmission quote is one labor
   * line and five parts, and it read as eight anonymous blocks: no
   * hierarchy, no grouping, double height for controls almost nobody
   * touches, and five of the eight lines off the bottom of a laptop.
   *
   * SO THE KIND BECOMES A HEADING INSTEAD OF A CONTROL. Lines are grouped
   * Labor, Parts, Fees, Discount, each with its own subtotal, and the
   * dropdown moves into a panel that opens when you ask for it. You almost
   * never change a line's kind; you read the split on every single quote,
   * which is why the totals block already had one.
   *
   * WHAT STAYS ON THE LINE is what you scan and what you type: what it is,
   * how many, the price, and what that line comes to. Cost and part number
   * sit under a part in muted text, borderless until you touch them, so
   * they are readable at a glance without looking like a form.
   *
   * Still tall rather than wide, still the same layout at every width. A
   * phone must never scroll sideways to reach the price box, which is the
   * one field somebody came here to type in.
   */
  const row = (l: LineItem) => `
    <div class="lrow k-${l.kind}" data-id="${l.id}">
      <!-- THE NAME GETS ITS OWN LINE, AND THE NUMBERS GET THE NEXT ONE.
           Anthony, 2026-09-24: "put the line items on top of the rest so
           you can see the line. It's okay to have the price lines like
           cost and total on the same lines as well as unit/hours. But
           that you can't read shit." On a desktop the name shared a row
           with the hours, the price, the total and two buttons, so it was
           the thing that got squeezed -- and the name is what the
           customer reads. Now it is the whole width, and everything you
           type a number into sits on one row under it, in the order it is
           worked out: how many, at what, what it cost us, comes to. -->
      <div class="lrow-top">
        <input class="li li-desc" value="${esc(l.description)}" aria-label="What it is"
          title="${esc(l.description)}">
      </div>
      <div class="lrow-nums">
        ${l.kind === "discount" ? "" : `
          <label class="lrow-qty"><span class="lrow-lab">${l.kind === "labour" ? "Hours" : "Qty"}</span>
            <input class="li li-qty n" type="number" step="0.25" inputmode="decimal"
              value="${l.quantity}" aria-label="${l.kind === "labour" ? "Hours" : "Quantity"}">
          </label>`}
        <label class="lrow-money"><span class="lrow-lab">${l.kind === "labour"
          ? (shopWords().caseFile ? "Rate per hour" : "Rate") : "Price"}</span>
          <span class="lrow-cur">$</span>
          <input class="li li-price n" type="number" step="0.01" inputmode="decimal"
            value="${l.unit_price}" aria-label="Customer price"${
            shopWords().caseFile && l.kind === "labour" ? ` min="${CASE_MIN_RATE}"` : ""}>
        </label>
        ${shopWords().caseFile && l.kind === "labour" ? `<span class="lrow-who">${
          esc(whoTag(Number(l.unit_price), Number(l.quantity)))}</span>` : ""}
        ${l.kind === "part" ? `
          <label class="lrow-cost" title="What the shop paid. Never shown to the customer.">
            <span class="lrow-lab">Cost</span><span class="lrow-cur">$</span>
            <input class="li li-cost n" type="number" step="0.01" inputmode="decimal"
              value="${l.cost ?? ""}" aria-label="What the shop paid"></label>` : ""}
        <span class="lrow-end">
          <b class="lrow-total">${money(l.total)}</b>
          <button class="lrow-open" title="Kind, and another one like this"
                  aria-label="More on this line">&hellip;</button>
          <button class="li-x" title="Remove this line" aria-label="Remove this line">&times;</button>
        </span>
      </div>

      <!-- WHAT THE SHOP PAID, ON THE PART IT PAID IT FOR. Muted and
           borderless until touched: an advisor reads these far more often
           than they type them, and a box round every one turns a quote
           into a form. -->
      ${l.kind === "part" ? `
        <div class="lrow-part">
          ${shopWords().partNumberLabel
            ? `<label>${esc(shopWords().partNumberLabel!)} <input class="li li-partno"
                 value="${esc(l.part_number ?? "")}"></label>` : ""}
          <!-- THE LAW, NOT A PREFERENCE. CCR 16 section 3356 makes the
               condition of each part a required element of the invoice.
               It starts empty and stays empty until somebody says, because
               a default of "new" would print a claim nobody made. -->
          ${shopWords().caseFile ? "" : `<label class="lrow-cond${asCondition(l.part_condition) ? "" : " lrow-cond--unsaid"}">condition
            <select class="li li-cond" title="Required on the invoice by CCR 16 section 3356">
              <option value="">not stated yet</option>
              ${conditionOptions().map((o) =>
                `<option value="${o.value}" ${l.part_condition === o.value ? "selected" : ""}>${
                  esc(o.text)}</option>`).join("")}
            </select></label>`}
        </div>` : ""}

      <!-- WHICH PAPER THIS CHARGE PRINTS ON. Only on a fee, which is what
           storage and towing are, and on anything already tagged so a
           charge sitting on the wrong kind stays visible and fixable.
           BAR wants towing and storage off the repair invoice: a customer
           who authorized a repair did not authorize a tow, and storage
           rides on the possessory lien rather than the repair contract. -->
      ${l.kind === "fee" || asBillAs(l.bill_as) !== "repair" ? `
        <div class="lrow-bill${asBillAs(l.bill_as) !== "repair" ? " lrow-bill--apart" : ""}">
          <label>bills
            <select class="li li-billas" title="Towing and storage go on their own document">
              ${billAsOptions().map((o) =>
                `<option value="${o.value}" ${asBillAs(l.bill_as) === o.value ? "selected" : ""}>${
                  esc(o.text)}</option>`).join("")}
            </select></label>
        </div>` : ""}

      <!-- OPENED, NOT SHOWN. Changing what a line IS happens once in
           fifty edits, and it was costing every row a dropdown. -->
      <div class="lrow-more">
        <label class="lbit"><span>Kind</span>
          <select class="li li-kind">${KINDS.map((k) =>
            `<option value="${k}" ${l.kind === k ? "selected" : ""}>${
              k === "labour" ? "labor" : k}</option>`).join("")}</select></label>
        <button class="li-copy" title="Another one like this">Another one like this</button>
      </div>
    </div>`;

  /**
   * One heading per kind, and only for kinds that have a line. An empty
   * "Fees" heading on every quote is the same clutter in a different place.
   */
  /**
   * EVERY GROUP IS ALWAYS THERE, AND EACH ONE ADDS ITS OWN.
   *
   * Anthony: "I want a cleaner quote area ... right now it's hard to read
   * stuff." The add buttons were a row of six under the lines, so adding a
   * part meant reading past the labor to a button that was not next to
   * any parts. Now Labor, Parts and Fees each carry their own "+ Add" in
   * their own heading, the way a bill is read, and an empty group says so
   * in one quiet line instead of disappearing -- a quote with no parts on
   * it should look like it has no parts, not like parts are not a thing.
   * Discount only appears when there is one; nobody starts a quote there.
   */
  const section = (kind: Kind, label: string, of: LineItem[], addWord?: string) => {
    const mine = of.filter((l) => l.kind === kind);
    if (!mine.length && !addWord) return "";
    /* A CASE FILE'S HOURS ARE PICKED BY HEAD COUNT, not typed as a rate.
       Laura, 2026-10-16: "it says hourly rate ... how do I fix this".
       The two buttons ARE the two rates, so nobody has to know them. */
    if (shopWords().caseFile && (kind === "labour" || kind === "fee")) {
      const sum0 = mine.reduce((n, l) => n + Number(l.total ?? 0), 0);
      const adds = kind === "labour"
        ? `<button class="lgroup-add" data-case-add="1">+ 1 investigator, ${money(CASE_MIN_RATE)}/hr</button>
           <button class="lgroup-add" data-case-add="2">+ 2 investigators, ${money(CASE_TWO_RATE)}/hr</button>`
        : `<button class="lgroup-add" data-case-add="car">+ Vehicle fee, ${money(CASE_VEHICLE_FEE)} a day</button>
           <button class="lgroup-add" data-add="fee" aria-label="Add another fee">+ Other fee</button>`;
      return `<div class="lgroup${mine.length ? "" : " empty-group"}">
        <div class="lgroup-head"><span>${kind === "labour" ? "Service" : "Fees"}</span>
          <b data-sub="${kind}">${money(sum0)}</b></div>
        <div class="lgroup-adds">${adds}</div>
        ${mine.length ? mine.map(row).join("")
          : `<div class="lgroup-none">${kind === "labour"
            ? "No hours yet. Pick one or two investigators above, then type the hours."
            : "No fees yet."}</div>`}
      </div>`;
    }
    const sum = mine.reduce((n, l) => n + Number(l.total ?? 0), 0);
    return `<div class="lgroup${mine.length ? "" : " empty-group"}">
      <div class="lgroup-head"><span>${label}</span>
        <b data-sub="${kind}">${money(sum)}</b>
        ${addWord ? `<button class="lgroup-add" data-add="${kind}"
            aria-label="Add ${esc(addWord)}">+ Add${
            kind === "labour" ? "" : ` ${esc(addWord)}`}</button>` : ""}</div>
      ${mine.length ? mine.map(row).join("")
        : `<div class="lgroup-none">No ${esc(label.toLowerCase())} yet.</div>`}
    </div>`;
  };

  const total = lines.reduce((n, l) => n + Number(l.total ?? 0), 0);
  const sums = {
    labour: lines.filter((l) => l.kind === "labour").reduce((n, l) => n + Number(l.total ?? 0), 0),
    part: lines.filter((l) => l.kind === "part").reduce((n, l) => n + Number(l.total ?? 0), 0),
    fee: lines.filter((l) => l.kind === "fee").reduce((n, l) => n + Number(l.total ?? 0), 0),
    // A discount is money off the total, so it has to be visible in the
    // split. Labor + parts that does not equal the total is a quote an
    // advisor stops trusting.
    discount: lines.filter((l) => l.kind === "discount")
      .reduce((n, l) => n + Number(l.total ?? 0), 0),
  };

  /**
   * THE PARTS NOBODY HAS SAID ANYTHING ABOUT.
   *
   * CCR title 16 section 3356 makes the condition of each part a required
   * element of the invoice, and the column starts empty on purpose so that
   * a person states it rather than the database assuming it. Which means
   * something has to ask, or 272 part lines stay blank forever and the
   * field was decoration.
   *
   * IT WARNS, IT DOES NOT BLOCK. Nothing here stops a quote being sent or
   * an invoice being raised. A missing condition is a thing to fix, not a
   * reason to leave a shop unable to bill a customer who is standing in
   * front of them, and a gate on the money path is how people learn to
   * click through warnings.
   *
   * It names the parts rather than counting them, because "two parts need
   * a condition" makes somebody hunt and "Alternator and Battery" does not.
   */
  const apart = separatePapersOn(lines);
  /* A case file's expenses are fees and searches, not parts with a
     condition (CCR 16 section 3356 is a repair-shop rule). */
  const needCondition = shopWords().caseFile ? [] : partsMissingCondition(lines);
  const nag = needCondition.length
    ? `<div class="lnag">
        <b>${needCondition.length === 1 ? `One ${shopWords().partLabel} has` : `${needCondition.length} ${shopWords().partsLabel} have`}
           no condition on ${needCondition.length === 1 ? "it" : "them"} yet.</b>
        Every part on the invoice has to say whether it is new, used,
        reconditioned, rebuilt or a crash part. Pick one under
        ${esc(needCondition.slice(0, 3).map((l) => String(l.description ?? "").trim() || `the unnamed ${shopWords().partLabel}`).join(", "))}${
          needCondition.length > 3 ? ` and ${needCondition.length - 3} more` : ""}.
      </div>`
    : "";

  /* A CHARGE THAT DOES NOT SAY WHAT IT IS FOR.
     Synchronous, unlike the EPA nag below, because it needs nothing but
     the lines already in hand. Flags 4 of the 284 charges on the book as
     of 2026-09-18 and leaves alone the five that mention supplies AND
     name something. See src/domain/shopsupplies.ts. */
  const vagueNag = suppliesNag(lines, money);

  host.innerHTML = `
    <div class="lineedit">
      ${shopWords().caseFile ? `<ol class="caseguide">
        <li><b>Service:</b> tap <i>1 investigator</i> (${money(CASE_MIN_RATE)} an hour) or
          <i>2 investigators</i> (${money(CASE_TWO_RATE)} an hour), then type the hours.
          A day is ${CASE_MIN_HOURS} hours minimum.</li>
        <li><b>Fees:</b> tap <i>Vehicle fee</i> once for each day in the field.</li>
        <li><b>Services</b> with a set price, like a background check, are one tap below.</li>
        <li>Anything else the client should know goes in <b>Additional information</b> under this.</li>
      </ol>` : ""}
      ${nag}
      ${vagueNag ? `<div class="lnag"><b>Say what the customer is paying for.</b>${esc(vagueNag)}</div>` : ""}
      <!-- WHO DISPOSED OF THE TOXIC WASTE. Filled in once the shop's
           row arrives, and left empty when there is nothing to say --
           which is most jobs and, until somebody sets an EPA id, all of
           them. Fire and forget like the price bar: a nag that has not
           arrived yet is a screen with one less thing on it, never a
           spinner. See src/domain/hazardous.ts. -->
      <div id="waste-nag"></div>
      <div class="lrows" id="lines-body">${
        section("labour", "Labor", lines, "labor")
        + section("part", cap(shopWords().partsLabel), lines, shopWords().partLabel)
        + section("fee", "Fees", lines, "fee")
        + section("discount", "Discount", lines)}</div>
      <!-- WHAT IT ADDS UP TO, split the way a customer reads a bill.
           A single grand total on the end of a button row answered "how
           much" and nothing else; the first question after that is always
           "how much of that is parts". -->
      <div class="ltotals">
        <div class="ltot"><span>${shopWords().caseFile ? "Service" : "Labor"}</span><b data-sum="labour">${money(sums.labour)}</b></div>
        <div class="ltot"><span>${esc(cap(shopWords().partsLabel))}</span><b data-sum="part">${money(sums.part)}</b></div>
        ${sums.discount ? `<div class="ltot"><span>Discount</span><b data-sum="discount">${
          money(sums.discount)}</b></div>` : ""}
        ${sums.fee ? `<div class="ltot"><span>Fees</span><b data-sum="fee">${
          money(sums.fee)}</b></div>` : ""}
        <div class="ltot grand"><span>Total</span><b data-sum="all">${money(total)}</b></div>
      </div>

      <!-- ADDING A LINE IS THE THING THIS SCREEN IS FOR, so the buttons
           that do it are the biggest thing on it rather than three small
           ones tucked beside a total. -->
      <!-- WHAT THE SHOP CHARGES, ON THE SCREEN RATHER THAN IN SOMEBODY'S
           HEAD. Filled in once the prices load; absent until then rather
           than flashing a wrong rate at somebody about to quote. -->
      <div class="pricebar" id="pricebar"></div>

      <!-- THE SHORTCUTS, QUIETER THAN THE ADDS. Lookups that fill a line
           in for you. They were full-size buttons in the same row as the
           adds, six things shouting at once; now they are one row of
           small ones under the total. -->
      <!-- NOT ON A CASE FILE. NYIA's quotes are their own service buttons
           above; "Book time" is a vehicle labor guide (trade.ts caseFile). -->
      ${shopWords().caseFile ? "" : `<div class="lineadd lineshort">
        <span class="lineshort-l">Fill in from</span>
        <button class="btn ghost sm" id="partpick">${esc(cap(shopWords().partsLabel))} we have ${esc(shopWords().hasEngine ? "fitted" : "used")}</button>
        ${canAskBookTime(ctx) ? `
          <button class="btn ghost sm" id="booktime">Book time</button>` : ""}
        <button class="btn ghost sm" id="labourpick">Times we have quoted</button>
      </div>`}

      <!-- STORAGE AND TOWING GET THEIR OWN BILL, and the button to raise
           it sits beside the lines rather than on the money panel, because
           this is the screen where somebody can see what is on it. Only
           offered for a paper that actually has money on it, so an empty
           "Bill the towing" never appears. -->
      ${apart.length && jobId ? `
        <div class="billapart">
          ${apart.map((paper) => `
            <button class="btn ghost sm" data-billapart="${paper}">Bill the ${
              billAsTitle(paper).toLowerCase()} separately, ${
              money(linesFor(lines, paper).reduce((n, l) => n + Number(l.total ?? 0), 0))}</button>`).join("")}
          <span class="fine">Towing and storage belong off the repair
            invoice, on their own document.</span>
        </div>` : ""}

      <!-- DRAFT THE WHOLE THING. Book time answers "how long"; this
           answers "what is the job" — labor, the parts it consumes, what
           the shop has paid for those parts before, and three sentences
           for the customer. It was the core of the old system and the
           rebuild had it down to a couple of line items. -->
      ${canAskBookTime(ctx) && jobId && !shopWords().caseFile ? `
        <!-- FOLDED ONCE THERE IS A QUOTE. The draft is how a bare job
             gets its first lines, so it is open when there are none; once
             there are, it is one line you can open, not a text box and a
             paragraph between the lines and the send button. -->
        <details class="draftfold" id="draftfold"${lines.length ? "" : " open"}>
          <summary><span class="draftfold-t">Draft it for me</span>
            <span class="draftfold-s">${shopWords().hasEngine
              ? "reads the conversation, the car's history and past part prices"
              : "reads the conversation and this customer's history here"}</span></summary>
          <!-- WHAT THE TECHNICIAN FOUND, BESIDE THE BUTTON THAT USES IT.
               Anthony: "I'd like a tech to be able to add a diagnostic
               under or near draft a quote to add or summarize for the ai
               to make off of. this would be seperate from ones that are
               already being made or automated without human contact."

               So it is not saved to the job and it is not a note. It is
               typed, used once, and gone: the thing a person says standing
               at the car, handed to the estimator and no further. Anything
               worth keeping belongs in the inspection or the complaint,
               where it is recorded and attributed to somebody. -->
          <label class="found">
            <span>What you found <i>optional</i></span>
            <textarea id="draft-found" rows="3">${
                esc(ctx?.found ?? "")}</textarea>
            <span class="found-fine">${ctx?.found
              ? `Filled in from the ${shopWords().techLabel}'s tests. Edit it if you like.`
              : "Used for this draft only. It is not saved to the job."}</span>
          </label>

        <div class="draftbar">
          <button class="btn primary" id="draftquote">Draft this quote</button>
          <span class="quiet small">${shopWords().hasEngine
            ? "Reads the conversation, this car's history here, the inspection and what the shop paid for parts last time."
            : `Reads the conversation and this customer's history here, then lists the services and hours. Your own rates are applied when you put it on the quote.`}</span>
        </div>
        <div id="draft-out"></div>
        </details>` : ""}


      <div id="booktime-out"></div>
      ${shopWords().caseFile ? "" : `<div class="fine">Cost is what the shop paid. The customer only sees the price.</div>`}
      <div class="msg-slot" id="line-msg"></div>
    </div>`;

  /**
   * PUT THE CURSOR IN THE LINE THAT WAS JUST ADDED.
   *
   * onChange() rebuilds the whole job card, and how long that takes
   * depends on the network — a fixed timeout guessed at it and lost. So
   * this WAITS for a row set bigger than the one it started with, checking
   * every 40ms and giving up after two seconds rather than hanging on a
   * save that failed.
   */
  const focusNewest = (was: number) => {
    const started = Date.now();
    const tick = () => {
      const rows = document.querySelectorAll<HTMLElement>("#lines-body .lrow");
      if (rows.length <= was) {
        if (Date.now() - started < 2000) setTimeout(tick, 40);
        return;
      }
      const last = rows[rows.length - 1];
      const box = last?.querySelector<HTMLInputElement>(".li-desc");
      if (!box) return;
      box.focus();
      box.select();
      last?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };
    tick();
  };

  /** How many rows there are right now, so the wait knows what to wait for. */
  const rowCount = () => document.querySelectorAll("#lines-body .lrow").length;

  const say = (e: unknown) => {
    const m = document.getElementById("line-msg");
    if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  };

  /**
   * THE RATE IN FORCE FOR THIS QUOTE.
   *
   * A local, not the account. The night rate applies to the job somebody
   * is quoting at eleven at night, not to every quote from then on.
   */
  let rate = labourRate;

  document.getElementById("booktime")?.addEventListener("click", () =>
    void askBookTime(ctx!, jobId, rate, onChange));

  document.getElementById("partpick")?.addEventListener("click", () =>
    partsSheet(jobId, tiers, onChange, ctx));

  document.getElementById("labourpick")?.addEventListener("click", () =>
    labourSheet(jobId, () => rate, onChange));

  /**
   * THE FIXED PRICES, AND THE SWITCH BETWEEN THE TWO HOURLY RATES.
   *
   * It opens on the night rate when it IS night, because the person typing
   * at eleven is quoting a night call-out, and having to remember to press
   * a button is exactly how a $300 hour goes out at $150.
   */
  /* The EPA nag, once the shop's row arrives. A failed read is null and
     null prints nothing, so a shop with no id set and a database without
     the column yet look the same from here -- neither breaks the screen
     and neither invents a number. */
  void shopEpaId().then((id) => {
    const slot = document.getElementById("waste-nag");
    if (!slot) return;
    const say = epaNag(lines, id);
    slot.innerHTML = say ? `<div class="lnag">${esc(say)}</div>` : "";
  });

  void Promise.all([loadPrices(), isFirstTimeCustomer(jobId).catch(() => false)])
    .then(([prices, firstTime]) => {
    const bar = document.getElementById("pricebar");
    if (!bar) return;

    const night = prices.nightRate;
    let onNight = Boolean(night) && isNightNow(prices.nightFrom, prices.nightTo);
    if (onNight && night) rate = night;

    const hour = (h: number) => (h === 0 ? "12am" : h < 12 ? `${h}am`
      : h === 12 ? "12pm" : `${h - 12}pm`);

    const paint = () => {
      bar.innerHTML = `
        <div class="pb-rate">
          ${shopWords().caseFile
            ? `<span>Services with a set price</span>`
            : `<span>Labor at <b>${money(rate)}</b> an hour</span>`}
          ${night ? `<button class="pb-switch" id="pb-night">${
            onNight ? `Back to the day rate ${money(prices.rate)}`
                    : `Night rate ${money(night)}`}</button>
            <span class="pb-when">${hour(prices.nightFrom)} to ${
              hour(prices.nightTo)}${onNight ? " — it is now" : ""}</span>` : ""}
        </div>
        ${prices.flat.length || firstTime ? `<div class="pb-flat">
          ${prices.flat.map((f, i) => `<button class="pb-fixed" data-flat="${i}">${
            esc(f.label)} <b>${money(f.price)}</b></button>`).join("")}
          ${firstTime ? `<button class="pb-fixed off" id="pb-first"
            title="For a first-time customer who says the price is too high. Hemet and San Jacinto especially.">
            First time customer <b>&minus;${money(FIRST_TIME_OFF)}</b></button>` : ""}
        </div>` : ""}`;

      document.getElementById("pb-night")?.addEventListener("click", () => {
        onNight = !onNight;
        rate = onNight && night ? night : prices.rate;
        paint();
      });

      // ONE PRESS, AND ONLY ONCE. A discount line already on the quote
      // means somebody has offered it; offering it twice is $50 off.
      document.getElementById("pb-first")?.addEventListener("click", async () => {
        const b = document.getElementById("pb-first") as HTMLButtonElement;
        if (document.querySelector('.lrow [value="discount"]')
          || lines.some((l) => l.kind === "discount")) {
          b.disabled = true; b.textContent = "Already on this quote";
          return;
        }
        b.disabled = true;
        const was = document.querySelectorAll(".lrow").length;
        try {
          // NEGATIVE UNIT PRICE, because `total` is a generated column:
          // quantity times unit price. A discount that is not negative is
          // a discount that adds to the bill.
          await saveLine(jobId, {
            kind: "discount", description: "First time customer",
            quantity: 1, unit_price: -FIRST_TIME_OFF, cost: null, part_number: null,
          });
          onChange();
          focusNewest(was);
        } finally {
          b.disabled = false;
        }
      });

      // A FIXED PRICE IS ONE PRESS. It goes on as labor at the price the
      // shop charges, quantity one — not as an hourly line somebody then
      // has to talk themselves out of editing.
      bar.querySelectorAll<HTMLElement>(".pb-fixed").forEach((b) => {
        b.addEventListener("click", async () => {
          const f = prices.flat[Number(b.dataset["flat"])];
          if (!f) return;
          const was = document.querySelectorAll(".lrow").length;
          b.setAttribute("disabled", "true");
          try {
            await saveLine(jobId, {
              kind: "labour", description: f.label, quantity: 1,
              unit_price: f.price, cost: null, part_number: null,
            });
            onChange();
            focusNewest(was);
          } finally {
            b.removeAttribute("disabled");
          }
        });
      });
    };
    paint();
  }).catch(() => { /* the editor works on the account rate alone */ });

  /**
   * THE WHOLE QUOTE, DRAFTED.
   *
   * Nothing is written until somebody has read it. The draft is shown with
   * its own numbers and a button; pressing that button is what puts lines
   * on the job. An estimator that writes straight into the quote is an
   * estimator whose mistakes arrive as facts.
   */
  document.getElementById("draftquote")?.addEventListener("click", async () => {
    const b = document.getElementById("draftquote") as HTMLButtonElement;
    const out = document.getElementById("draft-out");
    if (!out) return;
    b.disabled = true; b.textContent = "Reading everything…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/draft-quote"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          job_id: jobId,
          found: (document.getElementById("draft-found") as HTMLTextAreaElement | null)?.value ?? "",
        }),
      });
      const d = await res.json() as {
        error?: string;
        labour?: Array<{ task: string; hours: number; diagnostic: boolean }>;
        parts?: Array<{ name: string; cost: number; part_number: string; qty: number }>;
        notes?: string[]; seen_before?: string; caveat?: string;
        used?: { messages: number; pastVisits: number; inspectionFindings: number; knownPartCosts: number };
      };
      b.disabled = false; b.textContent = "Draft this quote";
      /* SAID WHERE THEY ARE LOOKING. The refusal used to land in a slot
         below the fold, so "Draft it for me" read as a dead button
         (Anthony, 2026-10-09: "nothing happens"). */
      if (!res.ok) {
        out.innerHTML = `<p class="msg bad">${esc(d.error ?? "That did not work.")}</p>`;
        out.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const labour = d.labour ?? [];
      const parts = d.parts ?? [];
      const u = d.used;

      out.innerHTML = `
        <div class="draft">
          <div class="draft-h">A draft, not a quote &mdash; nothing is on the job yet</div>
          ${d.seen_before ? `<p class="draft-seen">${esc(d.seen_before)}</p>` : ""}
          <!-- EDIT IT BEFORE IT LANDS. Anthony: "let me edit or remove
               items on the quote draft version before marking put it on
               the quote please."

               It was a read-only list with one button that took all of it.
               An estimator that is right about four lines out of five was
               therefore all-or-nothing: take the wrong one and delete it
               afterwards, or bin the lot and retype the four good ones.
               Both are worse than not offering the draft.

               Everything here is a field. Nothing is written to the job
               until Put it on the quote, so an edit costs nothing and a
               removal is a removal, not an undo. -->
          ${labour.length ? `<div class="draft-sec">Labor</div>
            ${labour.map((l, i) => `<div class="draft-row" data-dl="${i}">
              <input class="draft-t" value="${esc(l.task)}" aria-label="What the work is">
              <span class="draft-q"><input class="draft-h n" type="number" step="0.25"
                inputmode="decimal" value="${l.hours}" aria-label="Hours"><i>hr</i></span>
              <button class="draft-x" data-drop="l:${i}" aria-label="Remove">&times;</button>
            </div>`).join("")}` : ""}
          ${parts.length ? `<div class="draft-sec">${esc(cap(shopWords().partsLabel))} &mdash; what the shop pays</div>
            ${parts.map((p, i) => `<div class="draft-row" data-dp="${i}">
              <input class="draft-t" value="${esc(p.name)}" aria-label="What the ${esc(shopWords().partLabel)} is">
              <span class="draft-q"><i>&times;</i><input class="draft-qty n" type="number"
                step="1" inputmode="numeric" value="${p.qty}" aria-label="How many"></span>
              <span class="draft-q"><i>$</i><input class="draft-c n" type="number" step="0.01"
                inputmode="decimal" value="${p.cost}" aria-label="What the shop pays"></span>
              ${shopWords().partNumberLabel ? `<input class="draft-pn" value="${esc(p.part_number ?? "")}"
                aria-label="${esc(cap(shopWords().partNumberLabel!))}">` : ""}
              <button class="draft-x" data-drop="p:${i}" aria-label="Remove">&times;</button>
            </div>`).join("")}` : ""}
          ${(d.notes ?? []).length ? `<div class="draft-sec">For the customer</div>
            <textarea class="draft-note" id="draft-note" rows="3"
              aria-label="What the customer reads">${esc((d.notes ?? []).join(" "))}</textarea>` : ""}
          ${u ? (shopWords().hasEngine
            ? `<p class="draft-used">Read ${u.messages} messages, ${u.pastVisits} past
            ${u.pastVisits === 1 ? "visit" : "visits"} on this car, ${u.inspectionFindings}
            inspection ${u.inspectionFindings === 1 ? "finding" : "findings"} and
            ${u.knownPartCosts} known part costs.</p>`
            : `<p class="draft-used">Read ${u.messages} messages.</p>`) : ""}
          <p class="fine">${esc(d.caveat ?? "")}</p>
          <div class="next-do">
            <button class="btn primary" id="draft-take">Put it on the quote</button>
            <button class="btn ghost" id="draft-bin">Bin it</button>
          </div>
          <div class="msg-slot" id="draft-msg"></div>
        </div>`;

      document.getElementById("draft-bin")?.addEventListener("click", () => { out.innerHTML = ""; });

      /**
       * REMOVED MEANS GONE FROM THIS DRAFT, not hidden. The row is taken
       * out of the DOM and the read-back below walks what is left, so
       * there is no separate list of what was dropped to fall out of step
       * with what is on screen.
       */
      out.querySelectorAll<HTMLButtonElement>("[data-drop]").forEach((x) =>
        x.addEventListener("click", () => {
          x.closest(".draft-row")?.remove();
          const none = !out.querySelector("[data-dl],[data-dp]");
          const take = document.getElementById("draft-take") as HTMLButtonElement | null;
          if (take) {
            take.disabled = none;
            take.textContent = none ? "Nothing left to add" : "Put it on the quote";
          }
        }));

      document.getElementById("draft-take")?.addEventListener("click", async () => {
        const t = document.getElementById("draft-take") as HTMLButtonElement;
        const dm = document.getElementById("draft-msg");
        t.disabled = true; t.textContent = "Adding…";
        try {
          /**
           * WHAT IS ON SCREEN IS WHAT GOES ON, not what came back from the
           * estimator. Reading the boxes rather than the original answer
           * is the whole point: an edited hour, a corrected part number and
           * a removed line all have to survive the press.
           */
          const num = (el: Element | null, fallback = 0) => {
            const v = Number((el as HTMLInputElement | null)?.value);
            return Number.isFinite(v) ? v : fallback;
          };
          const text = (el: Element | null) =>
            ((el as HTMLInputElement | null)?.value ?? "").trim();

          for (const row of Array.from(out.querySelectorAll("[data-dl]"))) {
            const task = text(row.querySelector(".draft-t"));
            const hours = num(row.querySelector(".draft-h"));
            if (!task || !(hours > 0)) continue;
            await saveLine(jobId, {
              kind: "labour", description: task, quantity: hours,
              unit_price: rate, cost: null, part_number: null,
            });
          }
          for (const row of Array.from(out.querySelectorAll("[data-dp]"))) {
            const name = text(row.querySelector(".draft-t"));
            if (!name) continue;
            const cost = num(row.querySelector(".draft-c"));
            const qty = num(row.querySelector(".draft-qty"), 1) || 1;
            await saveLine(jobId, {
              kind: "part", description: name, quantity: qty,
              // Still priced by the shop's own bands, never by the
              // estimator: an edited COST goes through the same markup a
              // typed one does.
              unit_price: cost > 0 ? sellPrice(tiers, cost) : 0,
              cost: cost > 0 ? cost : null,
              part_number: text(row.querySelector(".draft-pn")) || null,
            });
          }
          const typedNote =
            (document.getElementById("draft-note") as HTMLTextAreaElement | null)?.value;
          const note = [typedNote ?? (d.notes ?? []).join(" "), d.seen_before]
            .filter(Boolean).join("\n\n").trim();
          if (note) await updateJobField(jobId, "quote_note", note);
          onChange();
        } catch (e) {
          t.disabled = false; t.textContent = "Put it on the quote";
          if (dm) {
            dm.innerHTML = `<p class="msg bad">${
              esc(e instanceof Error ? e.message : String(e))}</p>`;
          }
        }
      });
    } catch (e) {
      b.disabled = false; b.textContent = "Draft this quote";
      out.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      out.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });

  /**
   * A row no longer draws every box, so every read has to cope with the
   * box not being there. Quantity defaults to one, which is what a
   * discount or a fee is; cost stays undefined rather than null so an
   * absent field cannot wipe a figure that is stored.
   */
  const readRow = (tr: HTMLElement) => ({
    id: tr.dataset["id"] as string,
    kind: (tr.querySelector(".li-kind") as HTMLSelectElement).value as Kind,
    description: (tr.querySelector(".li-desc") as HTMLInputElement).value,
    quantity: Number((tr.querySelector(".li-qty") as HTMLInputElement | null)?.value) || 1,
    cost: (() => {
      const el = tr.querySelector(".li-cost") as HTMLInputElement | null;
      if (!el) return undefined;
      return el.value === "" ? null : Number(el.value);
    })(),
    unit_price: Number((tr.querySelector(".li-price") as HTMLInputElement).value) || 0,
    // It was hard-coded to null, so a part number typed into the old table
    // was discarded on save. Now it is a field like any other.
    // Only present on a part line, so the absent field must not wipe a
    // number that is already stored.
    part_number: (tr.querySelector(".li-partno") as HTMLInputElement | null)?.value.trim() ?? undefined,
    // Absent on a labour row, so undefined. Empty string means the
    // advisor picked "not stated yet" and that clears it to null.
    part_condition: (() => {
      const el = tr.querySelector(".li-cond") as HTMLSelectElement | null;
      if (!el) return undefined;
      return el.value === "" ? null : el.value;
    })(),
    // Absent on rows that do not draw it, so undefined leaves it alone.
    bill_as: (tr.querySelector(".li-billas") as HTMLSelectElement | null)?.value ?? undefined,
  });

  /**
   * WHY AN EDIT DOES NOT REDRAW THE PAGE.
   *
   * It used to. Every field's change event saved the line and then called
   * onChange(), which rebuilt the entire job card — and `change` on an
   * input fires on BLUR, so tabbing from Qty to Price destroyed the Price
   * box a fraction of a second after you arrived in it and threw away
   * what you were typing. On a job with several lines that reads exactly
   * as "the quote is glitching out", which is what Anthony hit on the PPI.
   *
   * So an edit now patches the two numbers that actually changed and
   * leaves every other node — and the focus — alone. Adding and removing
   * a line DO redraw the editor, because the row set really is different
   * and nobody is mid-field at that moment.
   */
  const repaintTotals = (): void => {
    let grand = 0;
    const by: Record<string, number> = { labour: 0, part: 0, fee: 0 };
    host.querySelectorAll<HTMLElement>(".lrow[data-id]").forEach((tr) => {
      // A discount line has no quantity box, and reading `.value` off the
      // null this returns threw the moment somebody put one on a quote.
      const qEl = tr.querySelector<HTMLInputElement>(".li-qty");
      const q = qEl ? Number(qEl.value) || 0 : 1;
      const p = Number((tr.querySelector(".li-price") as HTMLInputElement).value) || 0;
      const k = (tr.querySelector(".li-kind") as HTMLSelectElement).value;
      const line = Math.round(q * p * 100) / 100;
      grand += line;
      by[k] = (by[k] ?? 0) + line;
      const cell = tr.querySelector<HTMLElement>(".lrow-total");
      if (cell) cell.textContent = money(line);
    });
    // The split as well as the total, patched in place. Rebuilding the
    // editor to update a number is what made this glitch while somebody
    // was typing into it.
    for (const k of ["labour", "part", "fee", "discount"]) {
      const cell = host.querySelector<HTMLElement>(`[data-sum="${k}"]`);
      if (cell) cell.textContent = money(by[k] ?? 0);
      // The heading over the group carries the same number, so the two
      // must move together or the quote contradicts itself on screen.
      const head = host.querySelector<HTMLElement>(`[data-sub="${k}"]`);
      if (head) head.textContent = money(by[k] ?? 0);
    }
    const g = host.querySelector<HTMLElement>('[data-sum="all"]');
    if (g) g.textContent = money(grand);
    // Tell the page the money moved, so the figures outside this editor
    // and the next-step block can catch up without rebuilding the DOM
    // somebody is typing into.
    onTotals?.(grand);
  };

  /**
   * WHAT THE SHOP CALLS THIS WORK, offered as you type it.
   *
   * The list is the shop's own history first -- a part fitted before comes
   * with what was paid for it and its number -- and then the service
   * vocabulary built off the real book. Loading is fire and forget: a
   * suggestion list that has not arrived yet is a box you type into, which
   * is exactly what this screen was last week.
   */
  void loadVocabulary().then(() => {
    const fitted: Suggestion[] = [];
    void recentParts().then((rows) => {
      for (const r of rows) {
        fitted.push({
          label: r.description, from: "fitted",
          cost: r.cost, partNumber: r.part_number,
        });
      }
    });
    host.querySelectorAll<HTMLInputElement>(".li-desc").forEach((el) => {
      const row = el.closest(".lrow") as HTMLElement | null;
      typeahead(el, {
        done: () => fitted,
        onPick: (s) => {
          // A part that has been fitted brings its cost and its number
          // with it, because retyping those from a list that already knows
          // them is the work this is supposed to remove.
          if (!row || s.from === "reference") return;
          const costEl = row.querySelector<HTMLInputElement>(".li-cost");
          const pnEl = row.querySelector<HTMLInputElement>(".li-partno");
          if (costEl && s.cost != null && !costEl.value) {
            costEl.value = String(s.cost);
            costEl.dispatchEvent(new Event("input", { bubbles: true }));
          }
          if (pnEl && s.partNumber && !pnEl.value) pnEl.value = s.partNumber;
        },
      });
    });
  });

  host.querySelectorAll<HTMLElement>(".lrow[data-id]").forEach((tr) => {
    /**
     * CHANGING THE KIND CHANGES THE ROW.
     *
     * Switching a line to "part" used to save it and leave the row exactly
     * as it was — no cost box, no part number — so the two fields that
     * make a part a part could not be filled in without leaving the screen
     * and coming back. Redrawing is right here: the row genuinely is a
     * different shape, and nobody is mid-field at the moment they change a
     * dropdown.
     */
    tr.querySelector(".li-kind")?.addEventListener("change", async () => {
      try { await saveLine(jobId, readRow(tr)); onChange(); } catch (e) { say(e); }
    });

    tr.querySelectorAll<HTMLInputElement>(".li:not(.li-kind)").forEach((el) =>
      el.addEventListener("change", async () => {
        try {
          const r = readRow(tr);
          await saveLine(jobId, r);
          if (shopWords().caseFile && r.kind === "labour") {
            const p = tr.querySelector<HTMLInputElement>(".li-price");
            if (p && Number(p.value) > 0 && Number(p.value) < CASE_MIN_RATE) p.value = String(CASE_MIN_RATE);
            const tag = tr.querySelector<HTMLElement>(".lrow-who");
            if (tag) tag.textContent = whoTag(Number(p?.value), r.quantity);
          }
          repaintTotals();
          const m = document.getElementById("line-msg");
          if (m) m.innerHTML = "";
        } catch (e) { say(e); }
      }));

    // The suggestion, offered the moment a cost is typed and never applied
    // on its own. Clicking it fills the box; ignoring it costs nothing.
    const costEl = tr.querySelector<HTMLInputElement>(".li-cost");
    const priceEl = tr.querySelector<HTMLInputElement>(".li-price");
    costEl?.addEventListener("input", () => {
      const c = Number(costEl.value);
      const s = c > 0 ? sellPrice(tiers, c) : 0;
      let hint = tr.querySelector<HTMLElement>(".sugg");
      if (!hint) {
        hint = document.createElement("button");
        hint.className = "sugg";
        // Beside the cost that prompted it, on the part's own muted
        // line. It used to live in the panel that is now closed by
        // default, which would have made the suggestion invisible.
        // Beside the cost box itself, now that cost sits on the numbers row.
        (tr.querySelector(".lrow-cost") ?? tr.querySelector(".lrow-part"))?.after(hint);
        hint.addEventListener("click", () => {
          if (priceEl) { priceEl.value = String(hint!.dataset["v"]); priceEl.dispatchEvent(new Event("change")); }
        });
      }
      if (s > 0 && Math.abs(s - Number(priceEl?.value)) > 0.005) {
        hint.dataset["v"] = String(s);
        hint.textContent = `suggest ${money(s)}`;
        hint.hidden = false;
      } else hint.hidden = true;
    });

    tr.querySelector(".lrow-open")?.addEventListener("click", () => {
      tr.classList.toggle("open");
    });

    tr.querySelector(".li-x")?.addEventListener("click", async () => {
      try { await removeLine(tr.dataset["id"] as string); onChange(); } catch (e) { say(e); }
    });

    /**
     * ANOTHER ONE LIKE THIS. Four brake parts on one job differ by a word
     * and a price, and typing the kind, the quantity and the part number
     * four times is how a quote takes ten minutes instead of two.
     */
    tr.querySelector(".li-copy")?.addEventListener("click", async () => {
      const l = readRow(tr);
      const was = rowCount();
      try {
        await saveLine(jobId, {
          kind: l.kind, description: l.description, quantity: l.quantity,
          unit_price: l.unit_price, cost: l.cost,
          part_number: l.part_number ?? null,
        });
        onChange();
        focusNewest(was);
      } catch (e) { say(e); }
    });

    /**
     * ENTER MOVES ON. In the description it adds another line of the same
     * kind; anywhere else it saves and steps out. A quote is typed in a
     * rhythm and reaching for the mouse between every line breaks it.
     */
    tr.querySelector(".li-desc")?.addEventListener("keydown", async (e) => {
      if ((e as KeyboardEvent).key !== "Enter") return;
      e.preventDefault();
      const l = readRow(tr);
      const was = rowCount();
      try {
        await saveLine(jobId, {
          kind: l.kind,
          /* Laura, 2026-10-16: "change the word labor to service". A new
             row's name is printed on the client's quote as it stands. */
          description: l.kind === "labour" ? (shopWords().caseFile ? "Service" : "Labor") : l.kind === "part" ? cap(shopWords().partLabel) : "Fee",
          quantity: 1,
          unit_price: l.kind === "labour" ? rate : 0,
          cost: null, part_number: null,
        });
        onChange();
        focusNewest(was);
      } catch (err) { say(err); }
    });
  });

  /**
   * ADDING A LINE PUTS YOU IN IT.
   *
   * It used to add a row called "Labor" and leave the cursor wherever it
   * was, so every line took an add, a scroll, a click and a select-all
   * before a word could be typed. Now the new row's description is focused
   * and its placeholder text selected, so the next keystroke is the name
   * of the work — which is what somebody came here to type.
   */

  /**
   * RAISE THE SEPARATE BILL.
   *
   * It raises the document and stops. It does NOT move the job to
   * invoiced, because billing somebody for storage says nothing about
   * whether the work is finished, and it does not send anything: a
   * storage bill is usually the opening move in a conversation about a
   * car nobody has collected, and that conversation belongs to a person.
   */
  /* WHO AGREED TO THE TOW. Asked for only when there is a separate paper
     on this job, which is rare, and filled in after the screen has drawn
     rather than holding it up. BAR wants towing agreed on its own form,
     and something has to notice when that never happened. It says so and
     does not disable the button: a tow agreed on the phone at 11pm and
     written up next morning is the honest case, not the fraud. */
  if (apart.length && jobId) {
    void (async () => {
      try {
        const sb = await db();
        const { data } = await sb.from("job_authorizations")
          .select("authorizes,declined").eq("job_id", jobId);
        const missing = papersNeedingConsent(
          lines, (data ?? []) as Array<{ authorizes: string | null; declined: boolean }>);
        const slot = host.querySelector(".billapart .fine");
        if (slot && missing.length) {
          slot.textContent = missing.map(consentWanted).join(" ");
          slot.classList.add("bad");
        }
      } catch { /* the buttons still work without it */ }
    })();
  }

  host.querySelectorAll<HTMLButtonElement>("[data-billapart]").forEach((b) =>
    b.addEventListener("click", async () => {
      const paper = b.dataset["billapart"] as BillAs;
      const label = b.textContent ?? "";
      b.disabled = true; b.textContent = "Raising it...";
      try {
        await raiseInvoice(jobId, paper);
        onChange();
      } catch (e) {
        b.disabled = false; b.textContent = label;
        const slot = host.querySelector(".billapart .fine");
        if (slot) slot.textContent = e instanceof Error ? e.message : String(e);
      }
    }));

  /* The case-file adds: an hourly line at one of the two rates, starting
     on the five-hour day, or the vehicle fee. */
  host.querySelectorAll<HTMLButtonElement>("[data-case-add]").forEach((b) =>
    b.addEventListener("click", async () => {
      const what = b.dataset["caseAdd"];
      const was = rowCount();
      b.disabled = true;
      try {
        await saveLine(jobId, what === "car"
          ? { kind: "fee", description: "Vehicle fee (per day)", quantity: 1,
              unit_price: CASE_VEHICLE_FEE, cost: null, part_number: null }
          : { kind: "labour", description: "Investigation",
              quantity: CASE_MIN_HOURS, unit_price: what === "2" ? CASE_TWO_RATE : CASE_MIN_RATE,
              cost: null, part_number: null });
        onChange();
        focusNewest(was);
      } catch (e) { b.disabled = false; say(e); }
    }));

  host.querySelectorAll<HTMLButtonElement>("[data-add]").forEach((b) =>
    b.addEventListener("click", async () => {
      const kind = b.dataset["add"] as Kind;
      const was = rowCount();
      try {
        await saveLine(jobId, {
          kind,
          description: kind === "labour" ? (shopWords().caseFile ? "Service" : "Labor") : kind === "part" ? cap(shopWords().partLabel) : "Fee",
          quantity: 1,
          unit_price: kind === "labour" ? rate : 0,
          cost: null, part_number: null,
        });
        /**
         * THE NEW LINE SHOWS UP NOW, NOT AFTER THE WHOLE CARD RELOADS.
         *
         * Anthony, 2026-09-24: "It's also annoying that when I add a part
         * or labor I have to wait for a refresh for it to pop up." onChange()
         * rebuilds the entire job card, every query on it, before the row
         * exists. Only the lines changed, so only the lines are read back
         * (one query, which also picks up a tax line the save may have
         * added) and only this editor is redrawn. The figures outside it
         * catch up through onTotals, the same way an edit already does.
         *
         * The FIRST line on a quote still rebuilds the card, because that
         * is the one that changes what the rest of it offers -- the send
         * button only exists once there is something priced to send.
         */
        if (was === 0) { onChange(); focusNewest(was); return; }
        const fresh = await listLines(jobId);
        lineEditor(hostId, jobId, fresh, tiers, labourRate, onChange, ctx, onTotals);
        onTotals?.(fresh.reduce((n, l) => n + Number(l.total ?? 0), 0));
        focusNewest(was);
      } catch (e) { say(e); }
    }));
}

export { listLines };

/**
 * WHAT THIS SHOP HAS FITTED AND WHAT IT PAID, for the suggestion list.
 *
 * The same RPC the parts sheet uses, so a suggestion and the sheet can
 * never disagree about a price. Empty term means "everything, most used
 * first", which is what a typeahead filters down locally rather than
 * asking the database once per keystroke.
 */
async function recentParts(): Promise<Array<{
  description: string; cost: number | null; part_number: string | null;
}>> {
  try {
    const sb = await db();
    const { data } = await sb.rpc("parts_we_have_fitted", { p_term: "", p_limit: 40 });
    return ((data ?? []) as Array<{
      description: string; cost: number | null; part_number: string | null }>);
  } catch {
    return [];
  }
}

/**
 * PARTS THIS SHOP HAS ALREADY FITTED.
 *
 * Adding a part meant typing four things: what it is, its number, what it
 * cost, and what to charge. Four boxes, every time, for a part the shop
 * has fitted before and already knows the price of.
 *
 * THE CATALOG IS THE SHOP'S OWN RECEIPTS. Anthony asked whether there is
 * a free parts database anywhere with part numbers in it, and whether we
 * could crawl one. There is not: fitment data is licensed, so every catalog
 * carrying it keeps it behind a login, and the three obvious targets all
 * refuse by their own robots.txt. But the shop bought 1,965 parts this year
 * and kept the receipts, and 1,146 part numbers with the real price paid
 * are now in `parts_catalog` from the O'Reilly purchase detail.
 *
 * So this asks two places. A part the shop has FITTED wins, because it
 * carries what the customer was charged and the proof it went on a car. A
 * part it has only BOUGHT carries the number and the true cost, which is
 * most of the way there. The row says which, because they are worth
 * different amounts of confidence.
 *
 * IT SHOWS WHAT THE SHOP PAID LAST TIME, not an average. A price from
 * March is not what the counter charges today, and averaging the two is a
 * number that was never true on any day.
 *
 * The charge is worked out from the markup bands when it lands, not copied
 * from the old line, because the bands are where pricing decisions live
 * and a part that cost more this month should cost the customer more.
 */
function partsSheet(
  jobId: string, tiers: Tier[], onChange: () => void, ctx?: LabourContext,
): void {
  document.getElementById("partsheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="partsheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>${esc(cap(shopWords().partsLabel))} we know</h2>
          <div class="quiet">What the shop paid last time, from what it has
            fitted and from a year of counter receipts. The charge is worked
            out from your markup when it goes on.</div></div>
          <button class="sheet-x" id="ps-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          <label class="field"><span>Find ${esc(anA(shopWords().partLabel))}</span>
            <input id="ps-q"
                   autocomplete="off"></label>
          <!-- TWO PLACES A PART CAN COME FROM, and they answer different
               questions. What we have fitted knows what THIS shop paid.
               The counters know what is on a shelf this morning, at
               retail, which is a sanity check and not a cost. -->
          <div class="tabs" id="ps-tabs">
            <button class="tab on" data-src="mine">Fitted and bought</button>
            ${ctx?.vehicle ? `<button class="tab" data-src="live">At the counters</button>` : ""}
          </div>
          <div id="ps-list"><div class="loading">Reading&hellip;</div></div>
        </div>
        <div class="sheet-acts">
          <button class="btn ghost" id="ps-close">Done</button>
        </div>
      </div>
    </div>`);

  const shut = () => document.getElementById("partsheet")?.remove();
  document.getElementById("ps-x")?.addEventListener("click", shut);
  document.getElementById("ps-close")?.addEventListener("click", shut);

  type Seen = {
    description: string; part_number: string | null; cost: number | null;
    charged: number | null; times_used: number; last_used: string;
    source: "fitted" | "bought";
  };

  const draw = async (term: string): Promise<void> => {
    const list = document.getElementById("ps-list");
    if (!list) return;
    try {
      const sb = await db();
      const { data, error } = await sb.rpc("parts_we_have_fitted",
        { p_term: term, p_limit: 14 });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Seen[];
      if (!rows.length) {
        list.innerHTML = `<div class="empty">${term
          ? "Nothing like that has been fitted or bought here. Add it by hand "
            + "and it will be in this list next time."
          : `No ${shopWords().partsLabel} on any job yet.`}</div>`;
        return;
      }
      list.innerHTML = rows.map((r, i) => {
        const cost = Number(r.cost ?? 0);
        const charge = cost > 0 ? sellPrice(tiers, cost) : Number(r.charged ?? 0);
        /**
         * FITTED AND BOUGHT ARE DIFFERENT CLAIMS and the row says which.
         * "Fitted 3 times" means it went on three cars. "Bought 12" means
         * twelve came through the door and nothing here proves where they
         * went. An advisor pricing a job should be able to tell those
         * apart without opening anything.
         */
        const word = r.source === "bought" ? "bought" : "fitted";
        return `<button class="pspart" data-i="${i}">
          <span class="pspart-n">${esc(r.description)}</span>
          <span class="pspart-s">${r.part_number ? esc(r.part_number) + " · " : ""}${
            cost > 0 ? `cost ${money(cost)}` : "no cost on file"} · ${word} ${
            r.times_used} time${r.times_used === 1 ? "" : "s"}</span>
          <span class="pspart-p">${charge > 0 ? money(charge) : "—"}</span>
        </button>`;
      }).join("");

      list.querySelectorAll<HTMLButtonElement>(".pspart").forEach((b) =>
        b.addEventListener("click", async () => {
          const r = rows[Number(b.dataset["i"])];
          if (!r) return;
          b.disabled = true;
          const cost = Number(r.cost ?? 0);
          try {
            await saveLine(jobId, {
              kind: "part",
              description: r.description,
              quantity: 1,
              unit_price: cost > 0 ? sellPrice(tiers, cost) : Number(r.charged ?? 0),
              cost: cost > 0 ? cost : null,
              part_number: r.part_number ?? null,
            });
            // The sheet stays open. Four brake parts is four presses, not
            // four trips through a menu.
            b.classList.add("on");
            b.disabled = false;
            onChange();
          } catch {
            b.disabled = false;
          }
        }));
    } catch (e) {
      list.innerHTML = `<div class="empty bad">${
        esc(e instanceof Error ? e.message : String(e))}</div>`;
    }
  };

  /**
   * THE COUNTERS, LIVE.
   *
   * O'Reilly, AutoZone and NAPA first and in that order, because they are
   * a few minutes away and will hold a part for a van this morning — a
   * marketplace listing four dollars cheaper and three days out is not a
   * better answer for a mobile mechanic. Everything else is behind the
   * fold.
   *
   * THE PRICE IS RETAIL. It goes in the CHARGE box, never the cost box:
   * filling the shop's cost with what the public pays would run it through
   * the markup bands a second time and inflate every quote. An advisor
   * types what they actually paid once the part is on order.
   */
  const live = async (term: string): Promise<void> => {
    const list = document.getElementById("ps-list");
    if (!list || !ctx?.vehicle) return;
    if (!term) {
      list.innerHTML = `<div class="empty">Say which part &mdash; pads, alternator,
        serpentine belt &mdash; and this asks the counters for
        ${esc(ctx.vehicle)}.</div>`;
      return;
    }
    list.innerHTML = `<div class="loading">Asking the counters&hellip;</div>`;

    // The car, out of the one string the job carries: "2004 Ford F150".
    const bits = ctx.vehicle.trim().split(/\s+/);
    const year = /^(19|20)\d\d$/.test(bits[0] ?? "") ? bits.shift()! : "";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/parts-search"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          year, make: bits[0] ?? "", model: bits.slice(1).join(" "),
          part: term,
        }),
      });
      const d = await res.json() as {
        error?: string; source?: string;
        results?: Array<{
          title: string; brand: string; partNumber: string; price: number;
          vendor: string; local: boolean; supplier: string; link: string;
          supplierRank?: number;
        }>;
      };
      if (!res.ok || !d.results) {
        list.innerHTML = `<p class="msg bad">${esc(d.error ?? "That did not answer.")}</p>`;
        return;
      }
      const rows = d.results;
      if (!rows.length) {
        list.innerHTML = `<div class="empty">Nothing came back for that.</div>`;
        return;
      }
      const counters = rows.filter((r) => r.local);
      const web = rows.filter((r) => !r.local);

      // ONE PART, NOT NINE. The rule lives in domain/parts.ts, where it
      // can be tested: it decides what the shop buys.
      const { best, rest } = pickPart(counters);
      const row = (r: typeof rows[number], i: number) => `
        <button class="pspart${r.local ? " counter" : ""}" data-live="${i}">
          <span class="pspart-n">${esc(r.title)}</span>
          <span class="pspart-s">${r.local ? `<b>${esc(r.supplier)}</b> · ` : ""}${
            esc(r.vendor)}${r.partNumber ? ` · ${esc(r.partNumber)}` : ""}${
            r.brand ? ` · ${esc(r.brand)}` : ""}</span>
          <span class="pspart-p">${money(r.price)}</span>
        </button>`;

      list.innerHTML = `
        ${best
          ? `<div class="pshead">The one to use</div>
             ${row(best, rows.indexOf(best))}
             ${rest.length
               ? `<details class="psweb"><summary>Other counters &middot; ${
                    rest.length}</summary>
                    ${rest.map((r) => row(r, rows.indexOf(r))).join("")}</details>`
               : ""}`
          : `<div class="wt-trap">Nothing at O'Reilly, AutoZone or NAPA came back
               for this one. That is worth a phone call before believing it.</div>`}
        ${web.length
          ? `<details class="psweb"><summary>Online sellers &middot; ${web.length}</summary>
               ${web.map((r) => row(r, rows.indexOf(r))).join("")}</details>`
          : ""}
        <p class="fine">${d.source === "cache" ? "From the last lookup. " : ""}These are
          RETAIL prices, what the public pays. They go in the charge box, never in
          your cost &mdash; put what you actually pay in there and the markup does
          the rest.</p>`;

      list.querySelectorAll<HTMLButtonElement>("[data-live]").forEach((b) =>
        b.addEventListener("click", async () => {
          const r = rows[Number(b.dataset["live"])];
          if (!r) return;
          b.disabled = true;
          try {
            await saveLine(jobId, {
              kind: "part",
              description: [r.brand, r.title].filter(Boolean).join(" ").slice(0, 140)
                || r.title,
              quantity: 1,
              unit_price: r.price,
              // NOT the cost. See the note above this function.
              cost: null,
              part_number: r.partNumber || null,
            });
            b.classList.add("on");
            b.disabled = false;
            onChange();
          } catch { b.disabled = false; }
        }));
    } catch (e) {
      list.innerHTML = `<p class="msg bad">${
        esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  };

  let source: "mine" | "live" = "mine";
  const show = (term: string) => (source === "mine" ? void draw(term) : void live(term));

  document.querySelectorAll<HTMLButtonElement>("#ps-tabs .tab").forEach((b) =>
    b.addEventListener("click", () => {
      source = (b.dataset["src"] ?? "mine") as "mine" | "live";
      document.querySelectorAll("#ps-tabs .tab").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      show((document.getElementById("ps-q") as HTMLInputElement | null)?.value.trim() ?? "");
    }));

  let t: ReturnType<typeof setTimeout> | undefined;
  document.getElementById("ps-q")?.addEventListener("input", (e) => {
    clearTimeout(t);
    const v = (e.target as HTMLInputElement).value.trim();
    // The counters cost money to ask, so they wait for a pause worth
    // paying for. Our own history is free and can keep up with typing.
    t = setTimeout(() => show(v), source === "live" ? 700 : 200);
  });

  void draw("");
  (document.getElementById("ps-q") as HTMLInputElement | null)?.focus();
}

/**
 * WHAT THIS SHOP HAS QUOTED A JOB AT BEFORE.
 *
 * Anthony: "labor times from manuals we have in manuals side and
 * elsewhere." The manual side is answered by "Look up book time", which
 * asks the AI, because there is no ProDemand or ALLDATA API to call —
 * those are portals a person logs into.
 *
 * This is the "elsewhere", and on a job the shop has done before it is the
 * better answer of the two. An alternator quoted at 1.75 hours twice is
 * not an opinion about a Silverado, it is what THIS shop decided the work
 * takes, with its own people and its own van.
 *
 * IT SAYS "QUOTED", NOT "TOOK", and that distinction is not pedantry.
 * `pay_hours` holds 590 timesheets and not one of them carries a job id,
 * so the hours actually worked cannot be tied to the work they were
 * worked on. Until that link exists, claiming these are real durations
 * would be inventing evidence.
 *
 * The spread comes too. One-and-a-half to two hours is a different fact
 * from "1.75", and an advisor pricing a hard one should see it.
 */
function labourSheet(
  jobId: string, rateNow: () => number, onChange: () => void,
): void {
  document.getElementById("labsheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="labsheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>Times we have quoted</h2>
          <div class="quiet">What this shop has put on a quote before, and how
            often. Priced at the rate in force on this quote.</div></div>
          <button class="sheet-x" id="ls-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          <label class="field"><span>Find a job</span>
            <input id="ls-q"
                   autocomplete="off"></label>
          <div id="ls-list"><div class="loading">Reading&hellip;</div></div>
        </div>
        <div class="sheet-acts">
          <button class="btn ghost" id="ls-close">Done</button>
        </div>
      </div>
    </div>`);

  const shut = () => document.getElementById("labsheet")?.remove();
  document.getElementById("ls-x")?.addEventListener("click", shut);
  document.getElementById("ls-close")?.addEventListener("click", shut);

  type Past = {
    task: string; hours: number; low: number; high: number;
    times_used: number; last_used: string;
  };

  const draw = async (term: string): Promise<void> => {
    const list = document.getElementById("ls-list");
    if (!list) return;
    try {
      const sb = await db();
      const { data, error } = await sb.rpc("labour_we_have_quoted",
        { p_term: term, p_limit: 14 });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Past[];
      if (!rows.length) {
        list.innerHTML = `<div class="empty">${term
          ? "Nothing like that has been quoted here yet. Book time will have an "
            + "opinion, or type the hours and it will be in this list next time."
          : "No labor lines on any quote yet."}</div>`;
        return;
      }
      const rate = rateNow();
      list.innerHTML = rows.map((r, i) => {
        const spread = Number(r.low) !== Number(r.high)
          ? ` · ${Number(r.low)} to ${Number(r.high)} hr`
          : "";
        return `<button class="pspart" data-i="${i}">
          <span class="pspart-n">${esc(r.task)}</span>
          <span class="pspart-s">${Number(r.hours)} hr${spread} · quoted ${
            r.times_used} time${r.times_used === 1 ? "" : "s"}</span>
          <span class="pspart-p">${money(Number(r.hours) * rate)}</span>
        </button>`;
      }).join("");

      list.querySelectorAll<HTMLButtonElement>(".pspart").forEach((b) =>
        b.addEventListener("click", async () => {
          const r = rows[Number(b.dataset["i"])];
          if (!r) return;
          b.disabled = true;
          try {
            await saveLine(jobId, {
              kind: "labour", description: r.task, quantity: Number(r.hours),
              // The rate in force on THIS quote, which may be the night
              // rate. The hours are history; the money is today's.
              unit_price: rateNow(), cost: null, part_number: null,
            });
            b.classList.add("on");
            b.disabled = false;
            onChange();
          } catch { b.disabled = false; }
        }));
    } catch (e) {
      list.innerHTML = `<div class="empty bad">${
        esc(e instanceof Error ? e.message : String(e))}</div>`;
    }
  };

  let t: ReturnType<typeof setTimeout> | undefined;
  document.getElementById("ls-q")?.addEventListener("input", (e) => {
    clearTimeout(t);
    const v = (e.target as HTMLInputElement).value.trim();
    t = setTimeout(() => void draw(v), 200);
  });

  void draw("");
  (document.getElementById("ls-q") as HTMLInputElement | null)?.focus();
}

/** What a case-file hour line says beside its rate box. */
function whoTag(rate: number, hours: number): string {
  const who = investigatorsFor(rate);
  const short = hours > 0 && hours < CASE_MIN_HOURS ? ` · under the ${CASE_MIN_HOURS}-hour day` : "";
  if (who) return `For ${who}${short}`;
  return `${money(CASE_MIN_RATE)} minimum${short}`;
}
